import { HttpClient } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import currency from 'currency.js';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { NavigationReadsService } from '../../../services/navigation-reads.service';
import { AgentCommissionTotalReceivedDTO } from '../models/agent-commission-total-received.dto';
import { BalancesByDealsType, IBalance } from '../models/balance.interface';
import { Day } from '../models/day.type';
import { IFees } from '../models/fees.interface';
import { DateService } from './date.service';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  constructor(
    private httpClient: HttpClient,
    private dateService: DateService,
    @Optional() private reads: NavigationReadsService | null = null,
  ) {}

  public getBalance(onDate?: Date, sharePending = false): Observable<IBalance> {
    const load = () =>
      this.httpClient
        .get<IBalance>(`${environment.apiUrlToken}/api/accounting/balance`, {
          params: { onDate: onDate ?? new Date() } as any,
        })
        .pipe(
          map((b) => {
            b.available = currency(b.available);
            b.balance = currency(b.balance);
            b.fees = currency(b.fees);
            b.principal = currency(b.principal);
            return b;
          }),
        );
    return sharePending && !onDate && this.reads
      ? this.reads.readPending('accounting-current-balance', load)
      : load();
  }

  public getAgentCommissionTotalReceivedByYears(): Observable<
    AgentCommissionTotalReceivedDTO[]
  > {
    return this.httpClient
      .get<
        AgentCommissionTotalReceivedDTO[]
      >(`${environment.apiUrlToken}/api/accounting/agent-commission/total-received/by-years`)
      .pipe(
        map((list) => {
          return list.map((item) => {
            item.amount = currency(item.amount);
            return item;
          });
        }),
      );
  }

  public getBalanceByDeals(
    date?: Day,
    sharePending = false,
  ): Observable<BalancesByDealsType> {
    const load = () =>
      this.httpClient
        .get<BalancesByDealsType>(
          `${environment.apiUrlToken}/api/accounting/balances-by-deals`,
          {
            params: { date: date ?? this.dateService.nowDay() } as any,
          },
        )
        .pipe(
          map((data) => {
            for (const dataKey in data) {
              data[dataKey].balance = currency(data[dataKey].balance);
              const limit = data[dataKey].limit;
              data[dataKey].limit = limit == null ? undefined : currency(limit);
              data[dataKey].pendingPayments = currency(
                data[dataKey].pendingPayments,
              );
              data[dataKey].pendingPaymentsFee = currency(
                data[dataKey].pendingPaymentsFee,
              );
              data[dataKey].unpaidFees = currency(data[dataKey].unpaidFees);
              data[dataKey].unpaidPrincipal = currency(
                data[dataKey].unpaidPrincipal,
              );
              data[dataKey].unpaidTotal = data[dataKey].unpaidPrincipal.add(
                data[dataKey].unpaidFees,
              );
            }

            return data;
          }),
        );
    return sharePending && date === undefined && this.reads
      ? this.reads.readPending('accounting-current-ledger', load)
      : load();
  }

  public calculateFeeByAmount(
    date: Day,
    principal: currency,
    dealId: number,
  ): Observable<IFees> {
    return this.httpClient
      .get<IFees>(`${environment.apiUrlToken}/api/accounting/calculate-fees`, {
        params: {
          date: date.toString(),
          principal: principal.toString(),
          dealId: dealId?.toString(),
        },
      })
      .pipe(
        tap((data) => {
          data.fee = currency(data.fee);
          data.principal = currency(data.principal);
        }),
      );
  }

  public splitByAmount(
    date: Day,
    amount: currency,
    dealId: number,
  ): Observable<IFees> {
    return this.httpClient
      .get<IFees>(
        `${environment.apiUrlToken}/api/accounting/split-principal-fee`,
        {
          params: {
            date: date.toString(),
            amount: amount.toString(),
            dealId: dealId?.toString(),
          },
        },
      )
      .pipe(
        tap((data) => {
          data.fee = currency(data.fee);
          data.principal = currency(data.principal);
        }),
      );
  }
}
